import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Breakpoint for mobile detection (matches Tailwind's md breakpoint)
const MOBILE_BREAKPOINT = 768;

// Local storage key for persisting user preference
const STORAGE_KEY = 'girgit_view_mode_preference';

const ViewModeContext = createContext(undefined);

export function ViewModeProvider({ children }) {
  // Track if we're on a mobile device (auto-detected) — initialize from actual screen size
  const [isMobileDevice, setIsMobileDevice] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  
  // Track user's manual override preference: 'auto' | 'mobile' | 'desktop'
  const [userPreference, setUserPreference] = useState('auto');
  
  // The actual view mode being used — initialize from actual screen size
  const [viewMode, setViewMode] = useState(() => 
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop'
  );

  // Detect screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobileDevice(isMobile);
    };

    // Initial check
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Load user preference from localStorage on mount
  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem(STORAGE_KEY);
      if (savedPreference && ['auto', 'mobile', 'desktop'].includes(savedPreference)) {
        setUserPreference(savedPreference);
      }
    } catch (e) {
      console.warn('Could not access localStorage for view mode preference');
    }
  }, []);

  // Calculate effective view mode based on preference and device
  useEffect(() => {
    let effectiveMode;
    
    if (userPreference === 'auto') {
      // Auto mode: use device detection
      effectiveMode = isMobileDevice ? 'mobile' : 'desktop';
    } else {
      // Manual override
      effectiveMode = userPreference;
    }
    
    setViewMode(effectiveMode);
  }, [userPreference, isMobileDevice]);

  // Save preference to localStorage
  const savePreference = useCallback((preference) => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch (e) {
      console.warn('Could not save view mode preference to localStorage');
    }
  }, []);

  // Toggle between mobile and desktop (manual override)
  const toggleViewMode = useCallback(() => {
    const newPreference = viewMode === 'desktop' ? 'mobile' : 'desktop';
    setUserPreference(newPreference);
    savePreference(newPreference);
  }, [viewMode, savePreference]);

  // Set view mode directly
  const setViewModePreference = useCallback((preference) => {
    if (['auto', 'mobile', 'desktop'].includes(preference)) {
      setUserPreference(preference);
      savePreference(preference);
    }
  }, [savePreference]);

  // Reset to auto-detection
  const resetToAuto = useCallback(() => {
    setUserPreference('auto');
    savePreference('auto');
  }, [savePreference]);

  const value = {
    // Current effective view mode
    viewMode,
    // Whether currently showing mobile view
    isMobileView: viewMode === 'mobile',
    // Whether currently showing desktop view
    isDesktopView: viewMode === 'desktop',
    // Raw device detection result
    isMobileDevice,
    // User's preference setting
    userPreference,
    // Whether user has manually overridden
    isManualOverride: userPreference !== 'auto',
    // Actions
    toggleViewMode,
    setViewModePreference,
    resetToAuto,
  };

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode() {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
}

export default ViewModeContext;
